import React, { useState, useEffect, useCallback } from 'react';
import { paymentAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, Printer, TrendingUp } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, method: '', status: '', startDate: '', endDate: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 25 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [pd, sd] = await Promise.all([
        paymentAPI.getAll(params),
        paymentAPI.getSummary()
      ]);
      setPayments(pd.data.data);
      setPagination(pd.data.pagination);
      setSummary(sd.data.data);
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Payments</h2>
          <p className="text-muted text-small">All payment transactions</p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="stats-grid mb-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#d1fae5' }}>
              <TrendingUp size={20} color="#059669" />
            </div>
            <div className="stat-info">
              <p>Today's Collection</p>
              <h3>₹{fmt(summary.today?.amount)}</h3>
              <div className="trend">{summary.today?.count} transactions</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dbeafe' }}>
              <TrendingUp size={20} color="#2563eb" />
            </div>
            <div className="stat-info">
              <p>This Month</p>
              <h3>₹{fmt(summary.month?.amount)}</h3>
              <div className="trend">{summary.month?.count} payments</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fee2e2' }}>
              <TrendingUp size={20} color="#dc2626" />
            </div>
            <div className="stat-info">
              <p>Pending</p>
              <h3>{summary.pending}</h3>
              <div className="trend">awaiting collection</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <input type="date" className="form-control" style={{ width: 155 }}
          value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value, page: 1 }))} />
        <span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>to</span>
        <input type="date" className="form-control" style={{ width: 155 }}
          value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value, page: 1 }))} />
        <select className="form-control" style={{ width: 150 }}
          value={filters.method} onChange={e => setFilters(f => ({ ...f, method: e.target.value, page: 1 }))}>
          <option value="">All Methods</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="cheque">Cheque</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, method: '', status: '', startDate: '', endDate: '' })}>
          Clear
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr><th>Receipt</th><th>Date</th><th>Customer</th><th>For</th><th>Method</th><th>Txn ID</th><th>Amount</th><th>By</th><th></th></tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p._id}>
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{p.receiptNumber}</td>
                    <td style={{ fontSize: '.82rem' }}>{format(new Date(p.paymentDate), 'dd/MM/yy HH:mm')}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.customer?.name}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div>
                    </td>
                    <td style={{ fontSize: '.8rem', textTransform: 'capitalize' }}>{p.paymentFor?.replace(/_/g, ' ')}</td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod}</span></td>
                    <td className="font-mono" style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{p.transactionId || '—'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{p.collectedBy?.name}</td>
                    <td>
                      <a href={`/api/v1/payments/${p._id}/receipt`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                        <Printer size={12} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>
    </div>
  );
}
